import { QueryFactory } from '@lmt/shared-types';
import { DataBlockDictionaryItemDto } from '@lmt/shared-types';
import { InsuranceCompany } from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/constants';
import { DictionaryHookFormDropDownInput } from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/edit/DictionaryHookFormDropDownInput';
import {
  isColumnRequired,
  useDictionaryKeyTranslation,
  useInvalidateDependantsHandle,
} from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/helpers';
import {
  CustomDictionaryName,
  useDictionaryValuesById,
} from '@lmt/unicorn-shared/src/app/api/custom-hooks';
import { Error } from '@lmt/unicorn-shared/app/components/common/Error';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import styles from '../CustomDictionaryPage.module.scss';
import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { localizeDictionaryColumnTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';

type CreateOrEditDictionaryItemProps = FormComponentProps & {
  item?: DataBlockDictionaryItemDto;
  dictionaryId: number;
};

export const CreateOrEditDictionaryItem: FC<CreateOrEditDictionaryItemProps> = (
  props,
) => {
  const { formType, formId, tryCompleteForm } = props;

  const i18next = useScopedTranslation('custom_dictionary.create_edit');
  const i18nDictKey = useDictionaryKeyTranslation();
  const queryClient = useQueryClient();

  const dictionariesQuery = QueryFactory.DataBlockDictionaryQuery.useGetQuery();
  const currentDictionary = useMemo(
    () => dictionariesQuery.data?.find((x) => x.id === props.dictionaryId),
    [dictionariesQuery.data],
  );
  const { data: dictionaryValues } = useDictionaryValuesById(
    currentDictionary?.id,
  );

  const { invalidate: invalidateDependants } = useInvalidateDependantsHandle(
    currentDictionary?.id,
  );

  const localizedValue = useMemo(() => {
    if (props.item) {
      return i18nDictKey.t(props.item.value as any);
    }
  }, [props.item]);

  const localizedItem = useMemo(() => {
    if (props.item) {
      return {
        ...props.item,
        value: localizedValue ?? props.item.value,
      };
    }
  }, [localizedValue, props.item]);

  const form = useAdvancedForm<DataBlockDictionaryItemDto>(
    useCallback(
      async (data) => {
        await tryCompleteForm(
          formType,
          formId,
          async () => {
            let createdDictionary = null;
            const values = Object.assign({}, data.values); // this is needed if any column title is a number ('1')
            if (props.item) {
              const value = localizedValue ?? props.item.value;
              await QueryFactory.DataBlockDictionaryItemClient.patch(
                props.item.id,
                {
                  value: data.value === value ? props.item.value : data.value,
                  values: values,
                },
              );
            } else {
              const existingDeletedRow = dictionaryValues.find(
                (x) => x.value === data.value && x.isDeleted,
              );
              if (existingDeletedRow) {
                await QueryFactory.DataBlockDictionaryItemClient.patch(
                  existingDeletedRow.id,
                  {
                    value: data.value,
                    values: values,
                    isDeleted: false,
                  },
                );
              } else {
                createdDictionary =
                  await QueryFactory.DataBlockDictionaryItemClient.create({
                    dictionaryId: props.dictionaryId,
                    value: data.value,
                    values: values,
                  });
              }
            }

            await queryClient.invalidateQueries({
              queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
                props.dictionaryId,
              ),
            });
            await invalidateDependants();
            if (createdDictionary) {
              return createdDictionary;
            }
          },
          i18next.t('saving'),
        );
      },
      [formType, formId, tryCompleteForm, dictionaryValues, localizedValue],
    ),
    localizedItem,
    {
      shouldFocusError: true,
      criteriaMode: 'all',
      reValidateMode: 'onChange',
    },
  );

  const columnInputs = useMemo(() => {
    const columns = currentDictionary?.columns ?? [];
    return columns
      .sort((a, b) =>
        isColumnRequired(a, currentDictionary)
          ? isColumnRequired(b, currentDictionary)
            ? a.title.localeCompare(b.title)
            : -1
          : 1,
      )
      .map((column) => (
        <Field
          title={localizeDictionaryColumnTitle(currentDictionary, column.title)}
          required={isColumnRequired(column, currentDictionary)}
          className={globalFormStyles.field}
          key={column.title}
        >
          {column.referenceDictionaryId ? (
            <DictionaryHookFormDropDownInput
              control={form.control}
              required={isColumnRequired(column, currentDictionary)}
              name={`values.${column.title}`}
              dictionaryId={column.referenceDictionaryId}
              autoComplete={false}
              errorText={
                (form.formState.errors.values as any)?.[column.title]?.message
              }
            />
          ) : (
            <Input
              autoComplete="off"
              {...form.register(`values.${column.title}`, {
                setValueAs: (x: string) => x.trim(),
                ...(isColumnRequired(column, currentDictionary)
                  ? requiredRule()
                  : {}),
              })}
              errorText={
                (form.formState.errors.values as any)?.[column.title]?.message
              }
            />
          )}
        </Field>
      ));
  }, [currentDictionary?.id, form.formState]);

  /**
   * Insurance company value (name) is set automatically as combined 'short name' + 'number'
   */
  useEffect(
    function setInsuranceCompanyCombinedName() {
      if (
        currentDictionary?.title !== CustomDictionaryName.InsuranceCompanies
      ) {
        return;
      }
      const shortName = form.getValues(`values.${InsuranceCompany.ShortName}`);
      const number = form.getValues(`values.${InsuranceCompany.Number}`);
      if (shortName && number) {
        form.setValue('value', `${shortName}, ${number}`);
        form.clearErrors('value');
      } else {
        form.setValue('value', '');
      }
    },
    [
      form.watch(`values.${InsuranceCompany.Number}`),
      form.watch(`values.${InsuranceCompany.ShortName}`),
    ],
  );

  const isInsuranceCompanyDictionary =
    currentDictionary?.title === CustomDictionaryName.InsuranceCompanies;

  return (
    <form
      onSubmit={form.handleSubmitDefault}
      className={globalFormStyles.autoSizedContainer}
      data-test-id="editDictionaryItemPopup"
    >
      <div className={globalFormStyles.dataContainer}>
        <div className={styles.columnsTitle}>
          <Field
            title={i18next.t('value')}
            className={globalFormStyles.firstField}
            required={!isInsuranceCompanyDictionary}
          >
            <Input
              autoComplete="off"
              {...form.register('value', {
                setValueAs: (x: string) => x.trim(),
                ...requiredRule(),
              })}
              errorText={
                !isInsuranceCompanyDictionary || form.getValues('value') !== ''
                  ? form.formState.errors.value?.message
                  : undefined
              }
              autoFocus={true}
              // for insurance companies value is set automatically (combined short name + number)
              disabled={isInsuranceCompanyDictionary}
              // and that's why errorText and required option is chosen according to isInsuranceCompanyDictionary flag
            />
          </Field>
          {columnInputs}
        </div>
      </div>
      <div className={globalFormStyles.submitContainer}>
        <Error error={form.overallError} />
        <SubmitButton
          submitting={form.formState.isSubmitting}
          className={globalFormStyles.submit}
          title={
            props.item ? i18next.t('save_button') : i18next.t('create_button')
          }
        />
      </div>
    </form>
  );
};
